import EditorialAccessBoundary from 'app/components/EditorialAccessBoundary/EditorialAccessBoundary';
import ProgramAccessBoundary from 'app/components/EditorialAccessBoundary/ProgramAccessBoundary';
import Loading from 'app/components/Loading/LoadingScreen';
import { initFeatureFlag } from 'app/config/configFeatureFlag';
import { setSentryTenant } from 'app/config/configSentry';
import { initializeLogin } from 'app/config/initializeApp';
import { initializeLanguage } from 'app/config/initializeLanguage';
import { initializeLemonade } from 'app/config/initializeLemonade';
import { initializePersonalice } from 'app/config/initializePersonalice';
import EditorialAccessProvider from 'app/context/EditorialAccessProvider';
import RoutesContext from 'app/context/routes.context';
import useFeatureFlagRoutes from 'app/hooks/useFeatureFlagRoutes';
import useLastLocation from 'app/hooks/useLastLocation';
import useRestrictedPrograms from 'app/hooks/useRestrictedPrograms';
import { getEditorialNavigationScope } from 'app/models/editorialAccess';
import LinkLoginPage from 'app/pages/auth/LinkLoginPage/LinkLoginPage';
import LogoutPage from 'app/pages/auth/LogoutPage';
import HomePage from 'app/pages/home/HomePage';
import ScormNormalize from 'app/pages/scormNormalize';
import UIPage from 'app/pages/ui/UIPage';
import ViewerContainer from 'app/pages/viewer/ViewerContainer';
import ViewersContainer from 'app/pages/viewers/ViewersContainer';
import { canAccess } from 'app/policies/routes/routes.access';
import * as routerHelpers from 'app/services/router.service';
import { getRedirect } from 'app/services/routes.service';
import { app, scopes, systemSettings } from 'app/store/index';
import PropTypes from 'prop-types';
import React from 'react';
import { shallowEqual, useSelector } from 'react-redux';
import { matchPath, Redirect, Route, Switch, useLocation, withRouter } from 'react-router-dom';
import routes from './routesConfig';
import routesAuth, { PATH_LINK_LOGIN } from './routesConfigAuth';

function AuthorizedRoutes({ flags, settings, user }) {
  const restrictedPrograms = useRestrictedPrograms();
  const redirectUrl = getRedirect(user, { restrictedPrograms });

  return (
    <ProgramAccessBoundary>
      <Switch>
        <Route path="/home" render={() => <HomePage />} />
        <Route path="/ui" render={() => <UIPage />} />
        <Route path="/viewer/:id" render={() => <ViewerContainer />} />
        <Route path="/viewer-nutrient-standalone/:id" render={() => <ViewerContainer />} />
        <Route path="/viewer-v2/:id" render={() => <ViewerContainer />} />
        <Route path="/viewer-v1/:id" render={() => <ViewersContainer />} />
        <Route path="/scormnormalize" render={() => <ScormNormalize />} />

        {routes.map((item) =>
          canAccess({ flags, path: item.path, restrictedPrograms, settings, user }) ? <Route key={item.path} path={item.path} component={item.component} /> : null
        )}

        <Redirect from="*" to={redirectUrl} />
      </Switch>
    </ProgramAccessBoundary>
  );
}

AuthorizedRoutes.propTypes = { flags: PropTypes.object, settings: PropTypes.object, user: PropTypes.object };

export const Routes = withRouter(() => {
  const flags = useFeatureFlagRoutes();
  const SCOPES = useSelector((state) => scopes.selectors.getScopes(state));
  const isLoading = useSelector((state) => app.selectors.getLoading(state));
  const lastLocation = useLastLocation();
  const location = useLocation();
  const organization = useSelector((state) => systemSettings.selectors.getOrganization(state));
  const settings = useSelector((state) => systemSettings.selectors.getSetting(state));
  const [transitionPage, setTransistionPage] = React.useState('fadeIn'); // fadeIn, fadeOut
  const prevPathnameRef = React.useRef(location.pathname);
  const { authToken, isAuthorized, linkLoginCompleted, user } = useSelector(
    ({ auth }) => ({
      authToken: auth.authToken,
      isAuthorized: auth.user != null,
      linkLoginCompleted: auth.linkLoginCompleted,
      roleUser: auth.user?.role_guid || null,
      user: auth.user,
      userLastLocation: routerHelpers.getLastLocation(),
    }),
    shallowEqual
  );
  const redirectUrl = getRedirect(user);
  // No arrancar con la sesión guardada mientras el enlace valida su token: si está caducada, el 403 global
  // borraría el almacenamiento a mitad del acceso. Ligado al token para repetirse al cambiar de usuario.
  const isLinkLoginPending = Boolean(matchPath(location.pathname, { path: PATH_LINK_LOGIN, exact: true })) && !linkLoginCompleted;
  const sessionToken = isAuthorized && !isLinkLoginPending ? authToken : null;

  React.useEffect(() => {
    if (lastLocation && isAuthorized) {
      routerHelpers.saveLastLocation(lastLocation);
    }
  }, [lastLocation, isAuthorized]);

  // Manejar animación de transición cuando cambia la ruta
  React.useEffect(() => {
    if (location.pathname !== prevPathnameRef.current) {
      // Nueva ruta: iniciar fadeOut, luego fadeIn
      setTransistionPage('fadeOut');
      const timeout = setTimeout(() => {
        setTransistionPage('fadeIn');
      }, 150);

      prevPathnameRef.current = location.pathname;

      return () => clearTimeout(timeout);
    }
  }, [location.pathname]);

  React.useEffect(() => {
    if (isLinkLoginPending) return;

    initializeLanguage(user);
    if (sessionToken) {
      initializeLogin();
    }
  }, [isLinkLoginPending, sessionToken]);

  React.useEffect(() => {
    if (sessionToken) {
      initializeLemonade({ showLemonadeAiFeedback: flags?.showLemonadeAiFeedback, user });
    }
  }, [sessionToken, flags?.showLemonadeAiFeedback]);

  React.useEffect(() => {
    initializePersonalice({ organization, scopes: SCOPES });
  }, [organization]);

  React.useEffect(() => {
    if (organization) {
      setSentryTenant(organization);
    }
  }, [organization]);

  React.useEffect(() => {
    if (organization && user) {
      initFeatureFlag({ organization, user });
    }
  }, [organization, user]);

  return (
    <EditorialAccessProvider scopeKey={getEditorialNavigationScope(location.pathname)}>
      <RoutesContext.Provider value={{ transitionPage }}>
        <div>
          <Switch location={location}>
            <Route path="/logout" component={LogoutPage} />
            <Route path={PATH_LINK_LOGIN} exact component={LinkLoginPage} />

            {!isAuthorized ? (
              <Switch>
                {routesAuth.map((item) => (
                  <Route key={item.path} path={item.path} component={item.component} />
                ))}

                <Redirect to="/auth/login" from="/auth" exact={true} />
                <Redirect to="/auth/login" />
              </Switch>
            ) : (
              <Redirect from="/auth" to={redirectUrl} />
            )}

            {!isAuthorized ? (
              <Redirect to="/auth/login" />
            ) : (
              <EditorialAccessBoundary>
                <AuthorizedRoutes flags={flags} settings={settings} user={user} />
              </EditorialAccessBoundary>
            )}
          </Switch>
        </div>

        {isLoading && <Loading />}
      </RoutesContext.Provider>
    </EditorialAccessProvider>
  );
});
